import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppSettings } from "./providers/AppSettingsProvider";

export interface SubscriptionDetails {
  tier: "pro" | "platinum";
  period: "monthly" | "annual";
  expiryDate: string;
  willRenew: boolean;
}

// Combined Membership Card (Premium / Platinum / Free)
const MembershipCard: React.FC<{
  hasActiveMembership: boolean;
  details: SubscriptionDetails | null;
}> = ({ hasActiveMembership, details }) => {
  const { t, theme } = useAppSettings();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const tier = hasActiveMembership ? (details?.tier ?? "pro") : "free";
  const isPlatinum = tier === "platinum";
  const isPremium = tier === "pro";
  const isFree = tier === "free";

  const title = isPlatinum
    ? t('profile.subscription.tier.platinum')
    : isPremium
      ? t('profile.subscription.tier.pro')
      : t('profile.subscription.freePlanTitle');

  const iconSrc = isPlatinum
    ? "/images/platinum_icon.png"
    : isPremium
      ? "/images/pro_icon.png"
      : "/images/free_icon.png";

  const ctaText = isFree
    ? t('profile.membership.upgrade')
    : t('profile.membership.change');

  // Background gradient - dark mode (existing) vs light mode (light blue)
  const bgGradient = isDark
    ? (isPlatinum
      ? "bg-gradient-to-r from-[#102a8a] via-[#0c2376] to-[#0a1c61]"
      : isPremium
        ? "bg-gradient-to-r from-[#0d2b9e] via-[#1539b5] to-[#0b2380]"
        : "bg-gradient-to-r from-[#0d2b9e] via-[#1539b5] to-[#0b2380]")
    : (isPlatinum
      ? "bg-gradient-to-r from-[#e0f2fe] via-[#bae6fd] to-[#93c5fd]"
      : isPremium
        ? "bg-gradient-to-r from-[#dbeafe] via-[#bfdbfe] to-[#a5b4fc]"
        : "bg-gradient-to-r from-[#dbeafe] via-[#bfdbfe] to-[#a5b4fc]");

  // Border color
  const borderColor = isDark ? "border-white/10" : "border-blue-200/50";

  // Text colors
  const textColor = isDark ? "text-white" : "text-gray-800";
  const subtitleColor = isDark ? "text-white/80" : "text-gray-600";

  // Progress bar colors
  const barBg = isDark
    ? (isFree ? "bg-white/30" : "bg-white/20")
    : (isFree ? "bg-blue-200/50" : "bg-blue-200/40");
  const barFill = isDark
    ? (isFree ? "bg-white/70" : "bg-[#6fa5ff]")
    : (isFree ? "bg-blue-400" : "bg-blue-500");

  return (
    <div className={`relative overflow-hidden rounded-3xl ${bgGradient} border ${borderColor} shadow-xl p-4 md:p-5`}>
      {/* Row 1: icon + title + button */}
      <div className="flex items-center gap-3">
        <div className="shrink-0">
          <img
            src={iconSrc}
            alt={title}
            className="w-12 h-12 object-contain"
          />
        </div>

        <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
          <h3 className={`text-2xl font-semibold ${textColor} truncate`}>{title}</h3>
          <button
            onClick={() => navigate("/purchase")}
            className="shrink-0 rounded-full bg-[#4c7dff] hover:bg-[#3f6aef] text-white text-sm font-semibold px-4 py-2 transition-colors"
          >
            {ctaText}
          </button>
        </div>
      </div>

      {/* Row 2: subtitle */}
      <p className={`${subtitleColor} text-sm mt-3 truncate`}>
        {isFree
          ? t('profile.membership.freeSubtitle')
          : t('profile.membership.usageSubtitle', { minutes: 15 })}
      </p>

      {/* Row 3: progress bar */}
      <div className="mt-4">
        <div className={`h-2 rounded-full ${barBg} overflow-hidden`}>
          <div className={`h-full w-full ${barFill}`}></div>
        </div>
      </div>
    </div>
  );
};

export default MembershipCard;
